import type { RulesSpell } from '@/components/rules/SpellCard'
import type {
  Campaign, CampaignMember, CampaignSummary, CharacterWithStats, Invite, LedgerSummary,
  Encounter, InventoryView, Npc, PublicUser, Role, Roll, StoryEntry,
} from '@shared/types'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch {
    throw new ApiError('Could not reach the server. Is it running?', 0)
  }

  const text = await response.text()
  const body = text ? safeParse(text) : null

  if (!response.ok) {
    const message = (body as { error?: string } | null)?.error ?? `Request failed (${response.status})`
    const details = (body as { details?: Record<string, string[]> } | null)?.details
    throw new ApiError(message, response.status, details)
  }
  return body as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })

const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' })

export interface CampaignDetail {
  campaign: Campaign
  myRole: Role
  members: CampaignMember[]
  characters: CharacterWithStats[]
}

export interface InvitePreview {
  code: string
  campaignName: string
  dmName: string
  role: Exclude<Role, 'dm'>
  problem: string | null
}

export const api = {
  me: () => request<{ user: PublicUser | null }>('/auth/me'),
  login: (email: string, password: string) =>
    post<{ user: PublicUser }>('/auth/login', { email, password }),
  register: (input: { email: string; password: string; displayName: string; inviteCode?: string }) =>
    post<{ user: PublicUser; joinedCampaignId: string | null }>('/auth/register', input),
  logout: () => post<{ ok: true }>('/auth/logout'),
  updateProfile: (displayName: string) => patch<{ user: PublicUser }>('/auth/me', { displayName }),

  listCampaigns: () => request<{ campaigns: CampaignSummary[] }>('/campaigns'),
  createCampaign: (input: { name: string; blurb: string }) =>
    post<{ campaign: Campaign }>('/campaigns', input),
  getCampaign: (id: string) => request<CampaignDetail>(`/campaigns/${id}`),
  updateCampaign: (id: string, input: { name?: string; blurb?: string }) =>
    patch<{ ok: true }>(`/campaigns/${id}`, input),
  deleteCampaign: (id: string) => del<{ ok: true }>(`/campaigns/${id}`),

  setMemberRole: (campaignId: string, userId: string, role: 'player' | 'guest') =>
    patch<{ ok: true }>(`/campaigns/${campaignId}/members/${userId}`, { role }),
  removeMember: (campaignId: string, userId: string) =>
    del<{ ok: true }>(`/campaigns/${campaignId}/members/${userId}`),

  listInvites: (campaignId: string) => request<{ invites: Invite[] }>(`/campaigns/${campaignId}/invites`),
  createInvite: (campaignId: string, input: { role: 'player' | 'guest'; maxUses: number | null; expiresInDays: number | null }) =>
    post<{ invite: Invite }>(`/campaigns/${campaignId}/invites`, input),
  revokeInvite: (campaignId: string, code: string) =>
    del<{ ok: true }>(`/campaigns/${campaignId}/invites/${code}`),
  previewInvite: (code: string) => request<InvitePreview>(`/invites/${code}`),
  acceptInvite: (code: string) => post<{ campaignId: string }>(`/invites/${code}/accept`),

  getDashboard: (campaignId: string) =>
    request<{
      characters: CharacterWithStats[]
      encounter: { id: string; name: string; round: number; turnIndex: number; combatants: unknown[] } | null
      rolls: Roll[]
      balanceCp: number
      lastSession: { id: string; title: string; sessionDate: string | null } | null
      serverTime: number
    }>(`/campaigns/${campaignId}/dashboard`),

  assistantStatus: (campaignId: string) =>
    request<{ configured: boolean }>(`/campaigns/${campaignId}/assistant/status`),

  searchRules: (q: string, type = 'all', limit = 25) =>
    request<{ hits: { type: string; id: string; name: string; snippet: string }[] }>(
      `/rules/search?q=${encodeURIComponent(q)}&type=${type}&limit=${limit}`),
  listSpells: (filters: { level?: string; class?: string; school?: string } = {}) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value)
    return request<{ spells: RulesSpell[] }>(`/rules/spells?${params}`)
  },
  listRuleEntries: (category = 'all') =>
    request<{ entries: { id: string; category: string; name: string; body: string; meta: string }[] }>(
      `/rules/entries?category=${category}`),
  listRuleItems: (type = 'all') =>
    request<{ items: { id: string; name: string; type: string; rarity: string; properties: string; description: string }[] }>(
      `/rules/items?type=${type}`),

  getInventory: (campaignId: string) => request<InventoryView>(`/campaigns/${campaignId}/inventory`),
  addInventoryItem: (campaignId: string, input: Record<string, unknown>) =>
    post<InventoryView>(`/campaigns/${campaignId}/inventory`, input),
  updateInventoryItem: (campaignId: string, itemId: string, input: Record<string, unknown>) =>
    patch<InventoryView>(`/campaigns/${campaignId}/inventory/${itemId}`, input),
  deleteInventoryItem: (campaignId: string, itemId: string) =>
    del<InventoryView>(`/campaigns/${campaignId}/inventory/${itemId}`),

  getEncounter: (campaignId: string) =>
    request<{ encounter: Encounter | null }>(`/campaigns/${campaignId}/encounter`),
  startEncounter: (campaignId: string, input: { name: string; includeParty: boolean }) =>
    post<{ encounter: Encounter }>(`/campaigns/${campaignId}/encounter`, input),
  endEncounter: (campaignId: string) => del<{ ok: true }>(`/campaigns/${campaignId}/encounter`),
  addCombatant: (campaignId: string, input: Record<string, unknown>) =>
    post<{ encounter: Encounter }>(`/campaigns/${campaignId}/encounter/combatants`, input),
  updateCombatant: (campaignId: string, combatantId: string, input: Record<string, unknown>) =>
    patch<{ encounter: Encounter }>(`/campaigns/${campaignId}/encounter/combatants/${combatantId}`, input),
  removeCombatant: (campaignId: string, combatantId: string) =>
    del<{ encounter: Encounter }>(`/campaigns/${campaignId}/encounter/combatants/${combatantId}`),
  rollInitiative: (campaignId: string, all = false) =>
    post<{ encounter: Encounter; rolled: number }>(
      `/campaigns/${campaignId}/encounter/roll-initiative${all ? '?all=1' : ''}`),
  advanceTurn: (campaignId: string, back = false) =>
    post<{ encounter: Encounter }>(`/campaigns/${campaignId}/encounter/next${back ? '?back=1' : ''}`),

  listRolls: (campaignId: string, since = 0, limit = 50) =>
    request<{ rolls: Roll[]; serverTime: number }>(
      `/campaigns/${campaignId}/rolls?since=${since}&limit=${limit}`,
    ),
  createRoll: (campaignId: string, input: {
    formula: string; context?: string; characterId?: string | null
    advantage?: 'none' | 'advantage' | 'disadvantage'; private?: boolean
  }) => post<{ roll: Roll }>(`/campaigns/${campaignId}/rolls`, input),
  clearRolls: (campaignId: string) => del<{ ok: true }>(`/campaigns/${campaignId}/rolls`),

  listStory: (campaignId: string) => request<{ entries: StoryEntry[] }>(`/campaigns/${campaignId}/story`),
  createStory: (campaignId: string, input: Record<string, unknown>) =>
    post<{ id: string }>(`/campaigns/${campaignId}/story`, input),
  updateStory: (campaignId: string, entryId: string, input: Record<string, unknown>) =>
    patch<{ ok: true }>(`/campaigns/${campaignId}/story/${entryId}`, input),
  deleteStory: (campaignId: string, entryId: string) =>
    del<{ ok: true }>(`/campaigns/${campaignId}/story/${entryId}`),

  listNpcs: (campaignId: string) => request<{ npcs: Npc[] }>(`/campaigns/${campaignId}/npcs`),
  createNpc: (campaignId: string, input: Record<string, unknown>) =>
    post<{ id: string }>(`/campaigns/${campaignId}/npcs`, input),
  updateNpc: (campaignId: string, npcId: string, input: Record<string, unknown>) =>
    patch<{ ok: true }>(`/campaigns/${campaignId}/npcs/${npcId}`, input),
  deleteNpc: (campaignId: string, npcId: string) =>
    del<{ ok: true }>(`/campaigns/${campaignId}/npcs/${npcId}`),

  getLedger: (campaignId: string) => request<LedgerSummary>(`/campaigns/${campaignId}/ledger`),
  addLedgerEntry: (campaignId: string, input: {
    amount: number; coin: string; direction: 'gain' | 'spend'; reason: string
  }) => post<{ id: string }>(`/campaigns/${campaignId}/ledger`, input),
  deleteLedgerEntry: (campaignId: string, entryId: string) =>
    del<{ ok: true }>(`/campaigns/${campaignId}/ledger/${entryId}`),

  createCharacter: (campaignId: string, input: Record<string, unknown>) =>
    post<{ character: CharacterWithStats }>(`/campaigns/${campaignId}/characters`, input),
  importCharacter: (campaignId: string, payload: unknown) =>
    post<{ character: CharacterWithStats; matchedClass: boolean }>(
      `/campaigns/${campaignId}/characters/import`, payload,
    ),
  createFromDraft: (campaignId: string, draft: unknown) =>
    post<{ character: CharacterWithStats }>(`/campaigns/${campaignId}/characters/from-draft`, draft),
  getCharacter: (id: string) =>
    request<{ character: CharacterWithStats; myRole: Role; canEdit: boolean }>(`/characters/${id}`),
  updateCharacter: (id: string, input: Record<string, unknown>) =>
    patch<{ character: CharacterWithStats }>(`/characters/${id}`, input),
  deleteCharacter: (id: string) => del<{ ok: true }>(`/characters/${id}`),
  rest: (id: string, type: 'short' | 'long') =>
    post<{ character: CharacterWithStats; summary: string[]; type: string }>(
      `/characters/${id}/rest`, { type }),
  spendHitDie: (id: string) =>
    post<{ character: CharacterWithStats; rolled: number; healed: number; die: number }>(
      `/characters/${id}/hit-die`, {}),

  levelUp: (id: string, input: { hpMode: 'average' | 'roll' | 'manual'; hpGained?: number }) =>
    post<{ character: CharacterWithStats; gained: number; rolled: number | null; level: number }>(
      `/characters/${id}/level-up`,
      input,
    ),
}
